import { useEffect, useSyncExternalStore } from 'react';

/**
 * The docked chat's width, remembered per browser and shared by the two places
 * that must agree on it: the FloatingChat panel (its own `width`) and AppFrame
 * (the `--chat-w` var that reflows the content pane around it). Resizing updates
 * one store so both move in lockstep.
 *
 * The width the user drags is the CHAT's, not the page's: the page reflows around it
 * only while it still has room, and stops at a 900px floor after which the chat is drawn
 * over the page instead (globals.css `.orion-content-pane` min-width). So a wide drag is
 * always honoured here — nothing in this store knows about the floor.
 *
 * Web-only: a drag-to-resize side column has no counterpart on the phone apps,
 * where chat is always full-screen — so there is nothing to mirror on native.
 */
const STORAGE_KEY = 'symphony-chat-dock-width';

/** Matches CHAT_DOCK_WIDTH — the width before anyone has dragged. */
export const CHAT_DOCK_DEFAULT_WIDTH = 370;
export const CHAT_DOCK_MIN_WIDTH = 320;
export const CHAT_DOCK_MAX_WIDTH = 760;

/**
 * Keep at least this much of the page visible BESIDE the chat.
 *
 * Dragging past the point where the page hits its 900px floor no longer shrinks the page —
 * the chat slides over it (globals.css, `[data-brand='symphony'] .orion-content-pane`
 * min-width) — so this cap is the only thing left stopping a drag from burying the page
 * entirely on a small window. It is measured from the rail's inline-end edge, not the
 * window's: the rail is chrome, and counting its 260px as "visible page" let a 1000px
 * window be dragged to a chat that covered every last pixel of content.
 */
const MIN_VISIBLE_CONTENT = 260;

/** The rail's live width, which AppFrame mirrors onto :root as --sidebar-w. */
function railWidth(): number {
  if (typeof window === 'undefined' || typeof getComputedStyle !== 'function') return 0;
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--sidebar-w');
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function clampChatDockWidth(width: number): number {
  if (!Number.isFinite(width)) return CHAT_DOCK_DEFAULT_WIDTH;
  const viewportCap =
    typeof window !== 'undefined'
      ? Math.max(CHAT_DOCK_MIN_WIDTH, window.innerWidth - railWidth() - MIN_VISIBLE_CONTENT)
      : CHAT_DOCK_MAX_WIDTH;
  return Math.min(CHAT_DOCK_MAX_WIDTH, viewportCap, Math.max(CHAT_DOCK_MIN_WIDTH, Math.round(width)));
}

// SSR and the first client render both read the default, so the server HTML and
// the hydrated tree agree; the remembered value is applied in an effect after mount.
let current = CHAT_DOCK_DEFAULT_WIDTH;
let hydrated = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

export function setChatDockWidth(width: number): void {
  const next = clampChatDockWidth(width);
  if (next === current) return;
  current = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, String(current));
  } catch {
    // private mode / storage disabled — the width just won't persist.
  }
  emit();
}

export function resetChatDockWidth(): void {
  setChatDockWidth(CHAT_DOCK_DEFAULT_WIDTH);
}

function hydrateOnce(): void {
  if (hydrated) return;
  hydrated = true;
  let stored: number | null = null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    stored = raw == null ? null : clampChatDockWidth(Number(raw));
  } catch {
    stored = null;
  }
  // A viewport narrower than the remembered width re-clamps it down.
  const next = clampChatDockWidth(stored ?? current);
  if (next !== current) {
    current = next;
    emit();
  }
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Test-only reset of the in-memory store. */
export function __resetChatDockWidthStore(): void {
  current = CHAT_DOCK_DEFAULT_WIDTH;
  hydrated = false;
}

export function useChatDockWidth(): number {
  const width = useSyncExternalStore(subscribe, () => current, () => CHAT_DOCK_DEFAULT_WIDTH);
  useEffect(() => {
    hydrateOnce();
    // Keep the width sane if the window shrinks below it.
    const onResize = () => setChatDockWidth(current);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}
